/**
 * practice-decorations.tsx
 *
 * 1:1 从 Figma 导出的隨心練（PracticeHomePage）卡片装饰簇，按 `<html data-theme>` 切换配色。
 *
 * 单色装饰（recap-*、free-* 共 8 个）：每个只保留 **1 份去色几何 SVG**
 * （`src/assets/practice/shapes/*.svg`，fill 置 `currentColor`），以 `?raw` **内联渲染**，
 * 颜色查 `SHAPE_COLORS` 表 → 由外层 `color` 驱动 currentColor，8×4=32 个彩色 SVG 收敛为 8 个。
 * 注意：这些 SVG 含带 `transform` 的 `<rect>`，用 CSS mask 上色会被错误栅格化成实心块，
 * 故改用 currentColor 内联（渲染真实几何，与原 <img> 一致）。详见 CLAUDE.md。
 * 多色装饰（big-big）仍按主题各存一份 `{theme}/big-big.svg`，用 <img> 渲染。
 *
 * 每个装饰组件接收 `className`，期望父容器是 `position: relative` 的 card；
 * 内部用 `position: absolute` + 百分比定位，保持 Figma 原始版面比例。
 */

import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'

type Theme = 'orange' | 'green' | 'blue' | 'pink'

/* ---------- 主题感知 hook（无 Context、无依赖） ---------- */

function readTheme(): Theme {
  if (typeof document === 'undefined') return 'orange'
  const t = document.documentElement.getAttribute('data-theme') as Theme | null
  return t && (['orange', 'green', 'blue', 'pink'] as const).includes(t) ? t : 'orange'
}

function useCurrentTheme(): Theme {
  const [theme, setTheme] = useState<Theme>(readTheme)
  useEffect(() => {
    const obs = new MutationObserver(() => setTheme(readTheme()))
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] })
    return () => obs.disconnect()
  }, [])
  return theme
}

/* ---------- 资源映射（构建期内联） ---------- */

// 多色 big-big：按主题取彩色 SVG 的 URL（<img> 渲染）。keys 形如 ".../orange/big-big.svg"
const bigBigModules = import.meta.glob('../assets/practice/*/big-big.svg', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>

// 单色装饰：取去色几何 SVG 的源码（内联 + currentColor 上色）。keys 形如 ".../shapes/free-cos.svg"
const shapeRawModules = import.meta.glob('../assets/practice/shapes/*.svg', {
  eager: true,
  query: '?raw',
  import: 'default',
}) as Record<string, string>

function pick(modules: Record<string, string>, suffix: string): string {
  for (const k in modules) {
    if (k.endsWith(suffix)) return modules[k]
  }
  return ''
}

/** 多色装饰：按主题取彩色 SVG（big-big 专用） */
function asset(theme: Theme, name: string): string {
  return pick(bigBigModules, `/practice/${theme}/${name}.svg`)
}

/** 单色装饰：取去色几何 SVG 源码（fill=currentColor，颜色由外层 color 提供） */
function shapeRaw(name: string): string {
  return pick(shapeRawModules, `/practice/shapes/${name}.svg`)
}

/* ---------- 单色装饰配色矩阵（主题 × 装饰 → hex） ----------
   每个装饰是单色形状，4 套主题下仅填充色不同；几何只留 shapes/ 一份，颜色查此表。 */
type ShapeName =
  | 'recap-g33' | 'recap-vec' | 'recap-g32' | 'recap-g35'
  | 'free-cos' | 'free-sin' | 'free-msq' | 'free-g36'

const SHAPE_COLORS: Record<Theme, Record<ShapeName, string>> = {
  orange: { 'free-cos': '#E150EE', 'free-g36': '#5BC9E8', 'free-msq': '#FF7F7B', 'free-sin': '#FFE7D0', 'recap-g32': '#9CEA4F', 'recap-g33': '#FFD57B', 'recap-g35': '#E150EE', 'recap-vec': '#5BC9E8' },
  green:  { 'free-cos': '#FCBF93', 'free-g36': '#FB8159', 'free-msq': '#E150EE', 'free-sin': '#415111', 'recap-g32': '#415111', 'recap-g33': '#E150EE', 'recap-g35': '#FFD57B', 'recap-vec': '#FB8159' },
  blue:   { 'free-cos': '#AAAAAA', 'free-g36': '#E7FD93', 'free-msq': '#2248A4', 'free-sin': '#FB8159', 'recap-g32': '#E850A1', 'recap-g33': '#E7FD93', 'recap-g35': '#2248A4', 'recap-vec': '#FB8159' },
  pink:   { 'free-cos': '#FC683A', 'free-g36': '#FF6874', 'free-msq': '#E850A1', 'free-sin': '#AAAAAA', 'recap-g32': '#FEFCEA', 'recap-g33': '#FF6874', 'recap-g35': '#FFD57B', 'recap-vec': '#FC683A' },
}

// 单色装饰固有宽高比（来自 viewBox）：外层 span 无固有尺寸，
// 用 aspectRatio 复刻原 <img height:auto> 的等比高度，杜绝拉伸。
const SHAPE_RATIO: Record<ShapeName, string> = {
  'free-cos': '193 / 109', 'free-g36': '125 / 123', 'free-msq': '117 / 89', 'free-sin': '184 / 138',
  'recap-g32': '144 / 145', 'recap-g33': '132 / 140', 'recap-g35': '101 / 67', 'recap-vec': '121 / 115',
}

/* ---------- 卡片尺寸常量（来自 Figma 1920×1080 设计稿） ---------- */

// 中卡尺寸 703×397，Recap 装饰坐标（相对卡片左上角，Figma 1:1）。
// width = 导出 SVG 的固有宽度（设计像素，1:1），渲染时高度 auto 保持原始宽高比，
// 不再强制 height，避免被拉伸；left/top 已按「设计稿包围盒中心」回正。
const RECAP_W = 703
const RECAP_H = 397
const RECAP_ITEMS = [
  { name: 'recap-g33', left: 424.5, top: 0,     width: 132 },
  { name: 'recap-vec', left: 467,   top: 124.5, width: 121 },
  { name: 'recap-g32', left: 428.5, top: 199.5, width: 144 },
  { name: 'recap-g35', left: 438.5, top: 329,   width: 101 },
] as const

// 下卡尺寸 703×397，Free 装饰坐标（同上：width=SVG 固有宽，高度 auto）
const FREE_W = 703
const FREE_H = 397
const FREE_ITEMS = [
  { name: 'free-cos', left: 469.5, top: 214.5, width: 193 },
  { name: 'free-sin', left: 360,   top: 264,   width: 184 },
  { name: 'free-msq', left: 278,   top: 307.5, width: 117 },
  { name: 'free-g36', left: 575.5, top: 282.5, width: 125 },
] as const

/* ---------- 工具：percent 字符串 ---------- */

const p = (n: number, base: number) => `${(n / base) * 100}%`

/* ---------- 装饰组件 ---------- */

interface DecorProps {
  className?: string
}

/** 单个单色装饰：内联去色 SVG，currentColor 由 color 提供，按主题查表。
    left/width 按卡片宽 baseW 取百分比，top 按卡片高 baseH（与原 <img> 定位一致）。 */
function ShapeItem({ theme, name, left, top, width, baseW, baseH }: {
  theme: Theme; name: ShapeName; left: number; top: number; width: number; baseW: number; baseH: number
}) {
  return (
    <span
      aria-hidden="true"
      className="block [&>svg]:block [&>svg]:h-full [&>svg]:w-full"
      style={{
        position: 'absolute',
        left: p(left, baseW),
        top: p(top, baseH),
        width: p(width, baseW),
        aspectRatio: SHAPE_RATIO[name], // 复刻原 img height:auto 的等比高度
        color: SHAPE_COLORS[theme][name], // 驱动 SVG 内 fill=currentColor
      }}
      dangerouslySetInnerHTML={{ __html: shapeRaw(name) }}
    />
  )
}

/** 大卡「更多練習」底部装饰（占满卡片下半部分） */
export function BigCardDecor({ className }: DecorProps) {
  const theme = useCurrentTheme()
  const url = asset(theme, 'big-big')
  // big-big.svg 是 696×276，对应大卡 696×817 的底部 33.8%
  return (
    <img
      src={url}
      alt=""
      aria-hidden="true"
      draggable={false}
      className={cn('pointer-events-none select-none', className)}
      style={{
        position: 'absolute',
        left: 0,
        bottom: 0,
        width: '100%',
        // 高度由固有宽高比（696×276）决定，等比铺底、不拉伸
        height: 'auto',
      }}
    />
  )
}

/** Recap 卡「錯題重練」右侧 4 件装饰 */
export function RecapCardDecor({ className }: DecorProps) {
  const theme = useCurrentTheme()
  return (
    <div
      className={cn('pointer-events-none select-none', className)}
      style={{ position: 'absolute', inset: 0 }}
      aria-hidden="true"
    >
      {RECAP_ITEMS.map(it => (
        <ShapeItem key={it.name} theme={theme} name={it.name} left={it.left} top={it.top} width={it.width} baseW={RECAP_W} baseH={RECAP_H} />
      ))}
    </div>
  )
}

/** Free 卡「自由主題」右下 4 件装饰 */
export function FreeCardDecor({ className }: DecorProps) {
  const theme = useCurrentTheme()
  return (
    <div
      className={cn('pointer-events-none select-none', className)}
      style={{ position: 'absolute', inset: 0 }}
      aria-hidden="true"
    >
      {FREE_ITEMS.map(it => (
        <ShapeItem key={it.name} theme={theme} name={it.name} left={it.left} top={it.top} width={it.width} baseW={FREE_W} baseH={FREE_H} />
      ))}
    </div>
  )
}
